import moment from 'moment'
import { REHYDRATE } from 'redux-persist/lib/constants'
import {
  all,
  delay,
  fork,
  put,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects'

import { changeCurrentTheme } from './actions'

function calculateThemeFromSchedule(dateConfig) {
  if (!dateConfig?.themeLightStart || !dateConfig?.themeDarkStart) {
    return 'light'
  }

  const now = moment()
  const lightStart = moment(dateConfig.themeLightStart, 'HH:mm')
  const darkStart = moment(dateConfig.themeDarkStart, 'HH:mm')

  const currentTime = moment(now.format('HH:mm'), 'HH:mm')

  // Light starts before dark (e.g., 06:00 light, 18:00 dark)
  if (lightStart.isBefore(darkStart)) {
    if (
      currentTime.isSameOrAfter(lightStart) &&
      currentTime.isBefore(darkStart)
    ) {
      return 'light'
    }
    return 'dark'
  }

  // Dark starts before light (e.g., 18:00 light, 06:00 dark)
  if (lightStart.isAfter(darkStart)) {
    if (
      currentTime.isSameOrAfter(lightStart) ||
      currentTime.isBefore(darkStart)
    ) {
      return 'light'
    }
    return 'dark'
  }

  return 'light'
}

function* autoThemeWatcher() {
  while (true) {
    const { currentThemeSelected, dateConfig } = yield select(state => ({
      currentThemeSelected: state.theme.currentThemeSelected,
      dateConfig: state.dateConfig.data,
    }))

    if (currentThemeSelected === 'auto') {
      const calculatedTheme = calculateThemeFromSchedule(dateConfig)
      const currentTheme = yield select(state => state.theme.currentTheme)
      // Only update if calculated theme is different from current
      if (calculatedTheme !== currentTheme) {
        yield put(changeCurrentTheme(calculatedTheme))
      }
    }

    yield delay(60000) // Wait 1 minute before next check
  }
}

function* handleThemeSelection({ payload }) {
  const { currentThemeSelected } = payload

  if (currentThemeSelected === 'auto') {
    // Calculate and apply the theme immediately
    const dateConfig = yield select(state => state.dateConfig.data)
    const calculatedTheme = calculateThemeFromSchedule(dateConfig)

    yield put(changeCurrentTheme(calculatedTheme))
  } else {
    yield put(changeCurrentTheme(currentThemeSelected))
  }
}

function* handleRehydrate() {
  const { currentThemeSelected, dateConfig } = yield select(state => ({
    currentThemeSelected: state.theme.currentThemeSelected,
    dateConfig: state.dateConfig.data,
  }))

  if (currentThemeSelected === 'auto') {
    const calculatedTheme = calculateThemeFromSchedule(dateConfig)
    yield put(changeCurrentTheme(calculatedTheme))
  } else if (currentThemeSelected) {
    yield put(changeCurrentTheme(currentThemeSelected))
  }
}

export default all([
  fork(autoThemeWatcher),
  takeLatest('@theme/SET_CURRENT_THEME_SELECTED', handleThemeSelection),
  takeLeading(REHYDRATE, handleRehydrate),
  takeLeading('@dateConfig/SET', handleRehydrate),
])
